import LiquidText from '../components/LiquidText';
import { OrcamentoIcon } from '../components/OrcamentoUI';
import { useLiquidFeedback, LiquidDialog } from '../components/LiquidUI';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { fmtMoney, fmtValor, fmtInteiro } from '../utils/moeda';
import { kmsParaRevisao, alertaPorTratar, referenciaAlerta, prazoEmAlerta } from '../utils/alertasVeiculos';
import { useAlertasVeiculos } from '../context/AlertasVeiculosContext';
import './Veiculos.css';
import EmpresasManagerModal from '../components/EmpresasManagerModal';

// Servir as fotos guardadas (ficheiros em /uploads, fora do /api)
const uploadsBase = api.defaults.baseURL.replace(/\/api\/?$/, '');

/* Lê um montante escrito à portuguesa e devolve um número.

   O ponto é ambíguo: em "100.000,00" separa milhares, em "385.5" separa
   decimais. Desfaz-se assim: havendo vírgula, ela é a decimal e os pontos são
   milhares; não havendo, um único ponto seguido de uma ou duas casas é decimal
   e tudo o resto são milhares. Sem isto, "385.5" virava 3855 — dez vezes mais,
   e sem dar erro. */
const montanteParaNumero = (v) => {
    let t = String(v === undefined || v === null ? '' : v).replace(/[\s\u20AC]/g, '');
    if (t === '') return null;
    if (t.includes(',')) {
        t = t.replace(/\./g, '').replace(',', '.');
    } else if (!/^-?\d+\.\d{1,2}$/.test(t)) {
        t = t.replace(/\./g, '');
    }
    const n = Number(t);
    return Number.isFinite(n) ? n : NaN;
};

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

/* Desenho de uma carrinha de painel, para quando ainda não há fotografia.
   É um desenho nosso e não uma imagem do fabricante: fotografias de catálogo
   têm dono, e a foto que interessa mesmo é a da carrinha da casa. */
const SilhuetaCarrinha = () => (
    <svg viewBox="0 0 200 96" role="img" aria-label="Sem fotografia da viatura"
         style={{ width: '62%', height: 'auto', opacity: 0.5 }}>
        <path d="M14 70V44c0-4 2-7 6-8l22-6 12-14c2-2 4-3 7-3h56c4 0 7 2 8 6l8 21 32 6c5 1 9 5 9 10v14c0 3-2 5-5 5h-9"
              fill="none" stroke="currentColor" strokeWidth="4" strokeLinejoin="round" strokeLinecap="round" />
        <path d="M14 70h18M74 70h52" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
        <path d="M58 20v22H36" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinejoin="round" />
        <path d="M70 20v22h44l-8-22z" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinejoin="round" />
        <circle cx="53" cy="72" r="12" fill="none" stroke="currentColor" strokeWidth="4" />
        <circle cx="147" cy="72" r="12" fill="none" stroke="currentColor" strokeWidth="4" />
    </svg>
);

/* Carrinha de carga para o cartão das viaturas alugadas. Não é um desenho
   feito aqui como a silhueta de cima: é a imagem que o Tiago escolheu, em
   public/assets, com fundo transparente e recortada às margens. Fica mais
   pequena do que a moldura, ao centro: não é a fotografia de uma viatura, é o
   símbolo de uma categoria, e a folga à volta é o que faz essa diferença. */
const CarrinhaAlugada = () => (
    /* O "top" desloca só o desenho e não a caixa: a moldura centra o que lá
       está dentro, e uma margem seria repartida pelos dois lados — a carrinha
       descia metade do que se lhe pedisse. A 80% da largura da moldura sobram
       uns 25px acima e abaixo, pelo que estes 8px para baixo não a cortam. */
    <img src="/assets/carrinha-alugada.png" alt="Viaturas alugadas"
         style={{ width: '80%', height: 'auto', display: 'block',
             position: 'relative', top: 8 }} />
);

/* Chapa de matrícula portuguesa: banda azul da UE à esquerda, caracteres a
   preto sobre branco. Sem a tarja amarela da data de matrícula — a tabela
   veiculos não guarda essa data e não vale a pena inventá-la. */
const pontosEstrela = (cx, cy, R) => {
    const r = R * 0.382;
    return Array.from({ length: 10 }, (_, k) => {
        const a = (-90 + k * 36) * Math.PI / 180;
        const raio = k % 2 === 0 ? R : r;
        return `${(cx + raio * Math.cos(a)).toFixed(2)},${(cy + raio * Math.sin(a)).toFixed(2)}`;
    }).join(' ');
};

const ChapaMatricula = ({ matricula, titulo }) => {
    // Na chapa a sério os grupos vêm separados por pontos, não por hífenes.
    // O valor guardado não se toca: isto é só a forma de o mostrar.
    const grupos = String(matricula || '').trim().toUpperCase().split(/[\s.\-–—]+/).filter(Boolean);
    return (
        <span className="chapa" title={titulo || `Matrícula ${matricula}`}>
            <svg className="chapa-ue" viewBox="0 0 22 48" aria-hidden="true">
                <rect width="22" height="48" fill="#003399" />
                {Array.from({ length: 12 }, (_, i) => {
                    const a = (i * 30 - 90) * Math.PI / 180;
                    return <polygon key={i} fill="#ffcc00"
                        points={pontosEstrela(11 + 8 * Math.cos(a), 17 + 8 * Math.sin(a), 1.7)} />;
                })}
                <text x="11" y="40" fill="#fff" fontSize="13" fontWeight="700"
                    textAnchor="middle" fontFamily="Arial, Helvetica, sans-serif">P</text>
            </svg>
            <span className="chapa-num">
                {grupos.map((g, i) => (
                    <React.Fragment key={i}>
                        {i > 0 && <i className="chapa-sep" aria-hidden="true" />}
                        {g}
                    </React.Fragment>
                ))}
            </span>
        </span>
    );
};

/* Telemóvel com balão de mensagem. Desenhado aqui e não carregado como imagem:
   assim acompanha a cor do texto, fica nítido em qualquer tamanho e não traz
   um ficheiro atrás. */
const IconeMensagem = ({ tamanho = 18 }) => (
    <svg viewBox="0 0 24 24" width={tamanho} height={tamanho} aria-hidden="true" focusable="false"
         style={{ display: 'block', margin: '0 auto' }}>
        {/* o telemóvel, centrado e em traço: o auscultador em cima, o botão em
            baixo, e mais nada — a esta escala qualquer pormenor vira mancha */}
        <rect x="6.1" y="2.3" width="11.8" height="19.4" rx="2.4"
              fill="none" stroke="currentColor" strokeWidth="1.8" />
        <rect x="10.1" y="4.7" width="3.8" height="1.15" rx="0.57" fill="currentColor" />
        <rect x="10.3" y="18.6" width="3.4" height="1.2" rx="0.6" fill="currentColor" />
    </svg>
);

/* O pedido de confirmação a quem registou. Não acusa: pergunta. Quem o recebe
   está numa obra, e o que se quer dele é um número conferido, não uma defesa.
   Leva o que ele escreveu, para poder confirmar sem ir procurar nada. */
const textoConfirmacao = (viatura, linha) => {
    const nome = String(linha.quem || '').trim().split(/\s+/)[0];
    const dia = linha.data ? new Date(`${String(linha.data).slice(0, 10)}T00:00:00`).toLocaleDateString('pt-PT') : '';
    const num = (v, casas = 2) => Number(v).toFixed(casas).replace('.', ',');
    const partes = [`Olá${nome ? ' ' + nome : ''}, podes confirmar-me os dados deste registo?`, '',
        `Viatura: *${viatura}*`, `Data: *${dia}*`, `Quilómetros: *${num(linha.kms, 0)} km*`];
    if (linha.litros != null) partes.push(`Litros: *${num(linha.litros)} L*`);
    if (linha.valor != null) partes.push(`Valor: *${num(linha.valor)} €*`);
    partes.push('', linha.litros != null
        ? 'Confirma p.f. os quilómetros que estavam no conta-quilómetros e os litros abastecidos. Obrigado.'
        : 'Confirma p.f. os quilómetros que estavam no conta-quilómetros. Obrigado.');
    return partes.join('\n');
};

/* O número para o wa.me. Um 9xxxxxxxx português leva o indicativo à frente. */
const paraWhatsApp = (telefone) => {
    const d = String(telefone || '').replace(/\D/g, '');
    if (!d) return null;
    if (d.length === 9 && d.startsWith('9')) return '351' + d;
    return d;
};

/* "Francisco Ramos" → "F. R.". Só as iniciais, porque a coluna é estreita e o
   nome inteiro empurrava a data para fora; o nome completo fica no title, ao
   passar o rato. Nomes longos dão só a primeira e a última inicial — "José
   Manuel Tripa" é "J. T." e não "J. M. T.", que já não caberia. */
const iniciais = (nome) => {
    const partes = String(nome || '').trim().split(/\s+/).filter(Boolean);
    if (!partes.length) return '—';
    const escolhidas = partes.length === 1 ? [partes[0]] : [partes[0], partes[partes.length - 1]];
    return escolhidas.map(x => x[0].toUpperCase()).join('');
};

/* 9.8 → "9,80 L/100 km". A vírgula é decimal em português e o toFixed dá ponto;
   as duas casas ficam mesmo quando são zeros, para os números não dançarem de
   largura de linha para linha. */
const fmtConsumo = (v) => `${v.toFixed(2).replace('.', ',')} L/100 km`;

const Veiculos = () => {
    const liquidFeedback = useLiquidFeedback();
    const { user } = useAuth();
    const { recarregar: recarregarAlertas, confirmarTratado } = useAlertasVeiculos();
    const [veiculos, setVeiculos] = useState([]);
    const [frotaCarregada, setFrotaCarregada] = useState(false);
    const [erroFrota, setErroFrota] = useState(false);
    const alugadas = veiculos.filter(v => v.alugada);
    const [registos, setRegistos] = useState([]);
    const [empresas, setEmpresas] = useState([]);
    const [loading, setLoading] = useState(true);


    const navigate = useNavigate();
    const [captureMode, setCaptureMode] = useState(false);
    const isMobileUser = user?.perfil_mobile;

    // Modals
    const [showModal, setShowModal] = useState(false);
    /* O registo das alugadas: a lista fica atrás do cartão preto, no fim da
       fila. Separar não é enfeite — uma alugada não tem seguro, inspeção nem
       revisão da empresa, e as três linhas do resumo ficariam vazias em todas.

       De uma alugada só interessa uma coisa: quantos quilómetros fez entre
       abastecimentos, para se saber o consumo. Por isso a segunda vista abre
       os abastecimentos dela, agrupados por mês — havendo várias ao mesmo
       tempo, é a matrícula e o mês que separam os dados. */
    const [verAlugadas, setVerAlugadas] = useState(false);
    const [alugadaAberta, setAlugadaAberta] = useState(null);
    const [detalheAlugada, setDetalheAlugada] = useState(null);
    const [erroAlugada, setErroAlugada] = useState('');

    /* O cartão das alugadas mostra um mês de cada vez e abre no corrente, que é
       o que interessa a quem passa por ali. Os intervalos de todas vêm de uma
       vez e ficam aqui: trocar de mês é escolher entre o que já se tem, sem ir
       outra vez ao servidor.

       Tudo isto assenta nos abastecimentos lançados nas despesas — o
       colaborador paga com o cartão e diz a que viatura foi, com que
       quilómetros e quantos litros. Sem esses três, a viatura não aparece: não
       é que não tenha andado, é que não há como saber quanto. */
    const [mesesAlugadas, setMesesAlugadas] = useState(() => [new Date().getMonth() + 1]);
    const [anoAlugadas, setAnoAlugadas] = useState(() => new Date().getFullYear());
    const [intervalosAlugadas, setIntervalosAlugadas] = useState({});
    /* Os abastecimentos em cru. Os intervalos só nascem ao segundo; sem isto,
       o primeiro abastecimento de uma alugada não aparecia em lado nenhum. */
    const [abastecimentosAlugadas, setAbastecimentosAlugadas] = useState({});

    /* Meses soltos ou seguidos: o clique liga e desliga um, o clique com Shift
       apanha a faixa desde o último que se tocou. É por isso que fica aqui
       guardado qual foi — sem essa memória, o Shift não teria de onde contar. */
    const ultimoMesTocado = useRef(new Date().getMonth() + 1);
    const alternarMesAlugadas = (m, comShift) => {
        setMesesAlugadas(prev => {
            if (comShift) {
                const [a, b] = [ultimoMesTocado.current, m].sort((x, y) => x - y);
                const faixa = Array.from({ length: b - a + 1 }, (_, k) => a + k);
                return [...new Set([...prev, ...faixa])].sort((x, y) => x - y);
            }
            return prev.includes(m)
                ? prev.filter(x => x !== m)
                : [...prev, m].sort((x, y) => x - y);
        });
        ultimoMesTocado.current = m;
    };

    useEffect(() => {
        api.get('/veiculos/alugadas/intervalos')
            .then(r => setIntervalosAlugadas(r.data || {}))
            .catch(() => setIntervalosAlugadas({}));
        api.get('/veiculos/alugadas/abastecimentos')
            .then(r => setAbastecimentosAlugadas(r.data || {}))
            .catch(() => setAbastecimentosAlugadas({}));
    }, []);

    const abrirAlugada = async (v) => {
        setAlugadaAberta(v); setDetalheAlugada(null); setErroAlugada('');
        try {
            const r = await api.get(`/veiculos/${v.id}/consumo`);
            setDetalheAlugada(r.data);
        } catch (e) { setErroAlugada(e.response?.data?.error || e.message); }
    };

    /* Os intervalos por mês do abastecimento que os fechou. O total do mês não
       é a média das médias: quilómetros diferentes pesam diferente, por isso
       somam-se litros e quilómetros e só no fim se divide. */
    const porMes = useMemo(() => {
        const uteis = (detalheAlugada?.intervalos || []).filter(i => !i.excluido);
        const mapa = new Map();
        for (const i of uteis) {
            const chave = String(i.ate).slice(0, 7);
            if (!mapa.has(chave)) mapa.set(chave, { chave, linhas: [], km: 0, litros: 0 });
            const m = mapa.get(chave);
            m.linhas.push(i);
            m.km += i.km;
            m.litros += i.litros;
        }
        return [...mapa.values()]
            .sort((a, b) => b.chave.localeCompare(a.chave))
            .map(m => ({ ...m, consumo: m.km > 0 ? +(m.litros / m.km * 100).toFixed(2) : null }));
    }, [detalheAlugada]);

    /* Os anos que há para escolher: os que têm abastecimentos, mais o corrente
       — que tem de estar lá mesmo quando ainda ninguém abasteceu este ano,
       senão o cartão abria num ano que a caixa não oferece. */
    const anosAlugadas = useMemo(() => {
        const anos = new Set([new Date().getFullYear()]);
        for (const lista of Object.values(intervalosAlugadas))
            for (const i of lista) anos.add(Number(String(i.ate).slice(0, 4)));
        return [...anos].sort((a, b) => b - a);
    }, [intervalosAlugadas]);

    /* As alugadas que andaram nos meses escolhidos, com o consumo desse
       período. Uma viatura só aparece se lá tiver intervalos: não há datas de
       início e fim de aluguer, e ter andado é o que se sabe dela.

       Com vários meses o consumo é de todos eles juntos, não um por mês:
       somam-se os litros e os quilómetros de tudo o que caiu na escolha e só
       no fim se divide. Meses soltos somam na mesma — é a distância que pesa,
       não estarem lado a lado no calendário. */
    const alugadasDoPeriodo = useMemo(() => {
        const chaves = new Set(mesesAlugadas.map(m => `${anoAlugadas}-${String(m).padStart(2, '0')}`));
        return veiculos.filter(v => v.alugada).map(v => {
            const linhas = (intervalosAlugadas[v.id] || [])
                .filter(i => !i.excluido && chaves.has(String(i.ate).slice(0, 7)));
            const km = linhas.reduce((s, i) => s + i.km, 0);
            const litros = linhas.reduce((s, i) => s + i.litros, 0);
            /* Abastecimentos do período que ainda não fecharam consumo nenhum:
               ou são o primeiro da viatura, ou o seguinte ainda não chegou. */
            const fechados = new Set(linhas.map(i => String(i.ate).slice(0, 10)));
            const soltos = (abastecimentosAlugadas[v.id] || [])
                .filter(a => chaves.has(String(a.data).slice(0, 7)))
                .filter(a => !fechados.has(String(a.data).slice(0, 10)));
            return {
                v, km, litros, quantos: linhas.length, soltos,
                consumo: km > 0 ? litros / km * 100 : null,
            };
        }).filter(a => a.quantos > 0 || a.soltos.length > 0);
    }, [veiculos, intervalosAlugadas, abastecimentosAlugadas, mesesAlugadas, anoAlugadas]);
    const [showEmpresasManager, setShowEmpresasManager] = useState(false);
    const [editingVeiculo, setEditingVeiculo] = useState(null);

    // Form states
    const [formData, setFormData] = useState({
        nome: '',
        matricula: '',
        seguro: '',
        validade_seguro: '',
        validade_inspecao: ''
    });

    // New trip form
    const [newTrip, setNewTrip] = useState({
        data_viagem: new Date().toISOString().split('T')[0],
        veiculo_id: '',
        empresa_id: '',
        kms_percorridos: '',
        valor_portagens: '',
        destino: ''
    });

    // Filters
    const [filterVeiculo, setFilterVeiculo] = useState('all');
    const [filterMes, setFilterMes] = useState(new Date().getMonth() + 1);
    const [filterAno, setFilterAno] = useState(new Date().getFullYear());

    useEffect(() => {
        fetchVeiculos();
        fetchEmpresas();
        if (user?.perfil_mobile) {
            setCaptureMode(true);
        }
    }, [user]);

    useEffect(() => {
        fetchRegistos();
    }, [filterVeiculo, filterMes, filterAno]);

    const fetchVeiculos = async () => {
        setErroFrota(false);
        try {
            const res = await api.get('/veiculos');
            setVeiculos(res.data);
            // Auto-select first vehicle for new trip
            if (res.data.length > 0 && !newTrip.veiculo_id) {
                setNewTrip(prev => ({ ...prev, veiculo_id: res.data[0].id }));
            }
        } catch (error) {
            console.error('Erro ao buscar veículos:', error);
            setErroFrota(true);
        } finally { setFrotaCarregada(true); }
    };

    const fetchEmpresas = async () => {
        try {
            const res = await api.get('/veiculos/empresas/list');
            setEmpresas(res.data);
            // Auto-select "Sprod" or first company
            if (res.data.length > 0 && !newTrip.empresa_id) {
                const sprod = res.data.find(e => e.nome.toLowerCase().includes('sprod'));
                setNewTrip(prev => ({ ...prev, empresa_id: sprod ? sprod.id : res.data[0].id }));
            }
        } catch (error) {
            console.error('Erro ao buscar empresas:', error);
        }
    };

    const fetchRegistos = async () => {
        setLoading(true);
        try {
            const params = {
                veiculo_id: filterVeiculo,
                mes: filterMes,
                ano: filterAno
            };
            const res = await api.get('/veiculos/registos/all', { params });
            setRegistos(res.data);
        } catch (error) {
            console.error('Erro ao buscar registos:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (veiculo = null) => {
        if (veiculo) {
            setEditingVeiculo(veiculo);
            setFormData({
                nome: veiculo.nome,
                matricula: veiculo.matricula,
                seguro: veiculo.seguro || '',
                apolice: veiculo.apolice || '',
                validade_seguro: veiculo.validade_seguro ? veiculo.validade_seguro.split('T')[0] : '',
                validade_inspecao: veiculo.validade_inspecao ? veiculo.validade_inspecao.split('T')[0] : '',
                proxima_revisao: veiculo.proxima_revisao || '',
                // A data vem do servidor como ISO com hora; o input date só
                // aceita AAAA-MM-DD e ignora em silêncio o resto.
                proxima_revisao_data: String(veiculo.proxima_revisao_data || '').slice(0, 10)
            });
        } else {
            setEditingVeiculo(null);
            setFormData({
                nome: '',
                matricula: '',
                seguro: '',
                apolice: '',
                validade_seguro: '',
                validade_inspecao: '',
                proxima_revisao: '',
                proxima_revisao_data: ''
            });
        }
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingVeiculo) {
                await api.put(`/veiculos/${editingVeiculo.id}`, formData);
                liquidFeedback.notify('✅ Veículo atualizado!');
            } else {
                await api.post('/veiculos', formData);
                liquidFeedback.notify('✅ Veículo adicionado!');
            }
            setShowModal(false);
            fetchVeiculos();
            recarregarAlertas();   // o número na barra lateral tem de acompanhar
        } catch (error) {
            console.error('Erro ao salvar veículo:', error);
            liquidFeedback.notify('Erro ao salvar veículo');
        }
    };

    // Data de hoje em ISO, para os campos de data nascerem preenchidos.
    const hojeISO = () => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    const [aEnviarFoto, setAEnviarFoto] = useState(null);

    // ---- Leituras do conta-quilómetros ----
    const [modalKms, setModalKms] = useState(null);       // a viatura em causa
    const [kmsLista, setKmsLista] = useState([]);
    const [kmsForm, setKmsForm] = useState({ data: '', kms: '' });
    const [kmsErro, setKmsErro] = useState('');

    /* Dar um alerta por tratado. Guarda-se o valor que estava em alerta, para
       que uma data nova volte a acender o vermelho em vez de ficar escondida
       atrás de um "já tratei" antigo. */
    const marcarTratado = async (veiculo, campo) => {
        const referencia = referenciaAlerta(veiculo, campo);
        try {
            await api.post(`/veiculos/${veiculo.id}/alertas/${campo}`,
                { valor_referencia: referencia });
            setVeiculos(anteriores => anteriores.map(v => Number(v.id) === Number(veiculo.id)
                ? { ...v, alertas_tratados: { ...v.alertas_tratados, [campo]: referencia } } : v));
            confirmarTratado(veiculo, campo, referencia);
            await recarregarAlertas();
        } catch {
            liquidFeedback.notify('Não foi possível marcar como tratado.');
        }
    };

    const abrirKms = async (veiculo) => {
        setModalKms(veiculo);
        setKmsErro('');
        // Data de hoje por omissão: quase sempre é o dia em que se lê o conta-quilómetros.
        setKmsForm({ data: hojeISO(), kms: '' });
        try {
            const r = await api.get(`/veiculos/${veiculo.id}/kms`);
            setKmsLista(Array.isArray(r.data) ? r.data : []);
        } catch { setKmsLista([]); }
    };

    const gravarKms = async (e) => {
        e.preventDefault();
        setKmsErro('');
        try {
            await api.post(`/veiculos/${modalKms.id}/kms`, kmsForm);
            const lista = await api.get(`/veiculos/${modalKms.id}/kms`);
            setKmsLista(Array.isArray(lista.data) ? lista.data : []);
            setKmsForm(f => ({ ...f, kms: '' }));
            fetchVeiculos();   // o campo "Último registo kms" tem de acompanhar
            recarregarAlertas();
        } catch (err) {
            setKmsErro(err.response?.data?.error || 'Não foi possível registar.');
        }
    };

    // ---- Eventos da viatura ----
    const [modalEventos, setModalEventos] = useState(null);
    const [eventosLista, setEventosLista] = useState([]);
    const [eventoForm, setEventoForm] = useState({ data: '', descricao: '', valor: '' });
    const [eventoErro, setEventoErro] = useState('');

    const abrirEventos = async (veiculo) => {
        setModalEventos(veiculo);
        setEventoErro('');
        setEventoEmEdicao(null);
        setEventoForm({ data: hojeISO(), descricao: '', valor: '' });
        try {
            const r = await api.get(`/veiculos/${veiculo.id}/eventos`);
            setEventosLista(Array.isArray(r.data) ? r.data : []);
        } catch { setEventosLista([]); }
    };

    const gravarEvento = async (e) => {
        e.preventDefault();
        setEventoErro('');
        try {
            await api.post(`/veiculos/${modalEventos.id}/eventos`, eventoForm);
            const r = await api.get(`/veiculos/${modalEventos.id}/eventos`);
            setEventosLista(Array.isArray(r.data) ? r.data : []);
            setEventoForm(f => ({ ...f, descricao: '', valor: '' }));
        } catch (err) {
            setEventoErro(err.response?.data?.error || 'Não foi possível registar.');
        }
    };

    // { id, texto } enquanto uma linha do histórico está a ser corrigida.
    const [eventoEmEdicao, setEventoEmEdicao] = useState(null);

    const guardarEdicao = async () => {
        if (!eventoEmEdicao) return;
        const texto = eventoEmEdicao.texto.trim();
        if (!texto) { setEventoErro('Escreva o que aconteceu'); return; }
        setEventoErro('');
        try {
            await api.patch(`/veiculos/${modalEventos.id}/eventos/${eventoEmEdicao.id}`,
                { descricao: texto });
            setEventosLista(l => l.map(x => (x.id === eventoEmEdicao.id ? { ...x, descricao: texto } : x)));
            setEventoEmEdicao(null);
        } catch (err) {
            setEventoErro(err.response?.data?.error || 'Não foi possível alterar.');
        }
    };

    const apagarEvento = async (eventoId) => {
        try {
            await api.delete(`/veiculos/${modalEventos.id}/eventos/${eventoId}`);
            setEventosLista(l => l.filter(x => x.id !== eventoId));
        } catch { setEventoErro('Não foi possível apagar o evento.'); }
    };

    // ---- Consulta por período: kms ou eventos, por ano e mês ----
    const [modalConsulta, setModalConsulta] = useState(null);
    // Eventos por omissão: é a consulta que se faz mais vezes.
    /* Abre nos quilómetros, que é o que se vai ver quase sempre: os eventos
       (seguro, inspeção, revisão) mudam uma vez por ano, as leituras mudam
       todas as semanas. */
    const [consulta, setConsulta] = useState({ tipo: 'kms', ano: '', mes: '' });
    const [consultaLista, setConsultaLista] = useState([]);
    const [consultaAnos, setConsultaAnos] = useState({ kms: [], eventos: [] });
    const [consultaCarregando, setConsultaCarregando] = useState(false);

    const carregarConsulta = useCallback(async (veiculo, filtro) => {
        setConsultaCarregando(true);
        try {
            const r = await api.get(`/veiculos/${veiculo.id}/${filtro.tipo}`, {
                params: { ano: filtro.ano || undefined, mes: filtro.mes || undefined },
            });
            setConsultaLista(Array.isArray(r.data) ? r.data : []);
        } catch { setConsultaLista([]); }
        finally { setConsultaCarregando(false); }
    }, []);

    const abrirConsulta = async (veiculo) => {
        const inicial = { tipo: 'kms', ano: '', mes: '' };
        setModalConsulta(veiculo);
        setConsulta(inicial);
        try {
            const r = await api.get(`/veiculos/${veiculo.id}/periodos`);
            setConsultaAnos(r.data || { kms: [], eventos: [] });
        } catch { setConsultaAnos({ kms: [], eventos: [] }); }
    };

    // O filtro é aplicado assim que muda, sem botão de pesquisar pelo meio.
    useEffect(() => {
        if (modalConsulta) carregarConsulta(modalConsulta, consulta);
    }, [modalConsulta, consulta, carregarConsulta]);

    /* Só os anos que têm registos do tipo escolhido: oferecer anos vazios era
       convidar a pesquisas que não devolvem nada. */
    const anosDisponiveis = useMemo(
        () => consultaAnos[consulta.tipo] || [], [consultaAnos, consulta.tipo]);

    /* Soma dos custos dos eventos listados. Nulo quando nenhum deles tem valor:
       uma linha de total a zero num período só com inspeções não diz nada. */
    const totalEventos = useMemo(() => {
        if (consulta.tipo !== 'eventos') return null;
        const comValor = consultaLista.filter(r => r.valor != null);
        if (comValor.length === 0) return null;
        return comValor.reduce((soma, r) => soma + Number(r.valor), 0);
    }, [consulta.tipo, consultaLista]);

    /* Resumo só faz sentido nos quilómetros: nos eventos não há nada para somar.
       A lista vem da mais antiga para a mais recente, por isso os percorridos são
       a diferença entre a última linha e a primeira. */
    const resumoConsulta = useMemo(() => {
        if (consulta.tipo !== 'kms' || consultaLista.length === 0) return null;
        const primeira = consultaLista[0];
        const ultima = consultaLista[consultaLista.length - 1];
        return { n: consultaLista.length, primeira, ultima, percorridos: ultima.kms - primeira.kms };
    }, [consulta.tipo, consultaLista]);

    const apagarKms = async (registoId) => {
        try {
            await api.delete(`/veiculos/${modalKms.id}/kms/${registoId}`);
            setKmsLista(l => l.filter(x => x.id !== registoId));
            fetchVeiculos();
        } catch { setKmsErro('Não foi possível apagar o registo.'); }
    };


    const carregarFoto = async (veiculo, ficheiro) => {
        if (!ficheiro) return;
        if (!ficheiro.type.startsWith('image/')) { liquidFeedback.notify('Escolha um ficheiro de imagem.'); return; }
        setAEnviarFoto(veiculo.id);
        try {
            const fd = new FormData();
            fd.append('foto', ficheiro);
            const r = await api.post(`/veiculos/${veiculo.id}/foto`, fd);
            setVeiculos(vs => vs.map(v => (v.id === veiculo.id ? { ...v, foto_url: r.data.foto_url } : v)));
            setEditingVeiculo(v => (v && v.id === veiculo.id ? { ...v, foto_url: r.data.foto_url } : v));
        } catch (e) {
            liquidFeedback.notify(e.response?.data?.error || 'Não foi possível carregar a fotografia.');
        } finally { setAEnviarFoto(null); }
    };

    const removerFoto = async (veiculo) => {
        if (!await liquidFeedback.confirm(`Remover a fotografia de ${veiculo.nome}?`)) return;
        try {
            await api.delete(`/veiculos/${veiculo.id}/foto`);
            setVeiculos(vs => vs.map(v => (v.id === veiculo.id ? { ...v, foto_url: null } : v)));
            setEditingVeiculo(v => (v && v.id === veiculo.id ? { ...v, foto_url: null } : v));
        } catch { liquidFeedback.notify('Não foi possível remover a fotografia.'); }
    };

    const handleDelete = async (id, nome) => {
        if (!await liquidFeedback.confirm(`Eliminar o veículo ${nome}?`)) return;
        try {
            await api.delete(`/veiculos/${id}`);
            fetchVeiculos();
            fetchRegistos(); // Refresh trips list
        } catch (error) {
            console.error('Erro ao eliminar veículo:', error);
            liquidFeedback.notify('Erro ao eliminar veículo');
        }
    };

    const handleAddTrip = async (e) => {
        e.preventDefault();
        if (!newTrip.veiculo_id || !newTrip.empresa_id) {
            return liquidFeedback.notify('Selecione veículo e empresa');
        }

        try {
            await api.post('/veiculos/registos', {
                ...newTrip,
                utilizador_id: user?.id
            });
            // Reset form but keep date, vehicle, and company
            setNewTrip(prev => ({
                ...prev,
                kms_percorridos: '',
                valor_portagens: '',
                destino: ''
            }));
            fetchRegistos();
        } catch (error) {
            console.error('Erro ao adicionar viagem:', error);
            liquidFeedback.notify('Erro ao adicionar viagem');
        }
    };

    const handleDeleteTrip = async (id) => {
        if (!await liquidFeedback.confirm('Apagar este registo?')) return;
        try {
            await api.delete(`/veiculos/registos/${id}`);
            fetchRegistos();
        } catch (error) {
            liquidFeedback.notify('Erro ao apagar registo');
        }
    };

    const formatDate = (date) => {
        if (!date) return 'N/D';
        return new Date(date).toLocaleDateString('pt-PT');
    };

    // Calculate totals
    const totalKms = registos.reduce((sum, r) => sum + parseInt(r.kms_percorridos || 0), 0);
    const totalPortagens = registos.reduce((sum, r) => sum + parseFloat(r.valor_portagens || 0), 0);

    return (
        <div className="veiculos-container-new">
            {/* Top Bar Uniforme */}
            <div className="top-bar">
                <div className="top-bar-left">
                    <div className="top-bar-title">
                        <h1><OrcamentoIcon name="vehicle" fallback="🚗" /> Registo de Km's</h1>
                    </div>
                </div>
                <div className="top-bar-actions">
                    {!captureMode ? (
                        <>
                            <button className="btn-secondary mobile-hide" onClick={() => setShowEmpresasManager(true)}>
                                <OrcamentoIcon name="building" fallback="🏢" /> Empresas
                            </button>
                            <button className="btn-primary" onClick={() => setCaptureMode(true)}>
                                + <span className="mobile-hide">Novo Veículo</span><span className="mobile-only">Registar</span>
                            </button>
                        </>
                    ) : (
                        <>
                            {!isMobileUser && (
                                <button className="btn-secondary" onClick={() => setCaptureMode(false)}>
                                    ← Voltar
                                </button>
                            )}
                            {isMobileUser && (
                                <button className="btn-secondary" onClick={() => navigate('/mobile-dashboard')}>
                                    ← Menu
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Page Content */}
            <div className="page-content">
                {captureMode ? (
                    <div className="capture-view">
                        <div className="capture-card">
                            <h2 style={{ marginBottom: '20px', color: '#2d3748', fontSize: '1.2rem' }}><OrcamentoIcon name="vehicle" fallback="🚗" /> Registar Viagem</h2>
                            <form onSubmit={handleAddTrip}>
                                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '15px' }}>
                                    <div className="mobile-form-group">
                                        <label>Veículo</label>
                                        <select
                                            className="mobile-form-select"
                                            value={newTrip.veiculo_id}
                                            onChange={e => setNewTrip({ ...newTrip, veiculo_id: e.target.value })}
                                            required
                                        >
                                            <option value="">Selecione...</option>
                                            {veiculos.map(v => (
                                                <option key={v.id} value={v.id}>{v.nome}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="mobile-form-group">
                                        <label>Empresa</label>
                                        <select
                                            className="mobile-form-select"
                                            value={newTrip.empresa_id}
                                            onChange={e => setNewTrip({ ...newTrip, empresa_id: e.target.value })}
                                            required
                                        >
                                            {empresas.map(emp => (
                                                <option key={emp.id} value={emp.id}>{emp.nome}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="mobile-form-group">
                                    <label>Kms Percorridos</label>
                                    <input
                                        type="number"
                                        className="mobile-form-input"
                                        placeholder="0"
                                        value={newTrip.kms_percorridos}
                                        onChange={e => setNewTrip({ ...newTrip, kms_percorridos: e.target.value })}
                                        required
                                        style={{ fontSize: '1.5rem', fontWeight: 'bold' }}
                                    />
                                </div>
                                <div className="mobile-form-group">
                                    <label>Destino</label>
                                    <input
                                        type="text"
                                        className="mobile-form-input"
                                        placeholder="Ex: Lisboa"
                                        value={newTrip.destino}
                                        onChange={e => setNewTrip({ ...newTrip, destino: e.target.value })}
                                    />
                                </div>
                                <div className="mobile-form-group">
                                    <label>Portagens (€)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className="mobile-form-input"
                                        placeholder="0.00"
                                        value={newTrip.valor_portagens}
                                        onChange={e => setNewTrip({ ...newTrip, valor_portagens: e.target.value })}
                                    />
                                </div>

                                <button type="submit" className="btn-large-submit">
                                    Guardar Registo
                                </button>
                            </form>
                        </div>

                        {registos.length > 0 && (
                            <div className="mobile-recent-list">
                                <h3 style={{ margin: '0 0 15px 0', color: '#718096', fontSize: '0.9rem', textTransform: 'uppercase' }}>Últimos Registos</h3>
                                {registos.slice(0, 3).map(reg => (
                                    <div key={reg.id} className="mobile-recent-item">
                                        <div className="mobile-recent-row">
                                            <div className="mobile-recent-info">
                                                <h4>{reg.veiculo_nome}</h4>
                                                <div className="mobile-recent-meta">{formatDate(reg.data_viagem)} • {reg.destino || 'Sem destino'}</div>
                                            </div>
                                            <div className="mobile-recent-value">
                                                {reg.kms_percorridos} km
                                            </div>
                                        </div>
                                        <div className="mobile-recent-actions">
                                            <button
                                                className="btn-mobile-action delete"
                                                onClick={() => handleDeleteTrip(reg.id)}
                                            >
                                                <OrcamentoIcon name="trash" fallback="🗑️" /> Apagar
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <>
                        {/* VEHICLES SECTION (30%) */}
                        <div className="vehicles-section">
                            <h3 style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: '#666' }}>Veículos Registados</h3>
                            {/* Quantos cartões vão na fila — as viaturas próprias mais o
                                cartão das alugadas. É com este número que o CSS reparte a
                                largura toda em colunas iguais, em vez de as deixar a meio
                                do ecrã. */}
                            {!frotaCarregada ? <div className="vehicles-loading" role="status">A carregar a frota…</div> : erroFrota ? <div className="vehicles-loading" role="alert">Não foi possível carregar a frota. <button className="btn-secondary" onClick={fetchVeiculos}>Tentar novamente</button></div> : <div className="vehicles-grid-compact"
                                 style={{ '--n-cartoes': veiculos.filter(v => !v.alugada).length + 1 }}>
                                {veiculos.filter(v => !v.alugada).map(veiculo => (
                                    <div key={veiculo.id} className="vehicle-item">
                                        {/* O cartão inteiro abre a edição. Como é uma zona
                                            clicável e não um botão verdadeiro (leva imagem e
                                            texto lá dentro), precisa do papel e do teclado
                                            à mão. */}
                                        <div className="vehicle-card"
                                            role="button" tabIndex={0}
                                            title={`Editar ${veiculo.nome}`}
                                            onClick={() => handleOpenModal(veiculo)}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    handleOpenModal(veiculo);
                                                }
                                            }}>
                                            {/* A fotografia manda no cartão: numa frota pequena
                                                reconhece-se a carrinha muito antes de se ler a
                                                matrícula. Sem foto, fica o desenho por omissão. */}
                                            <div className="vehicle-foto">
                                                {veiculo.foto_url
                                                    ? <img src={`${uploadsBase}${veiculo.foto_url}`} alt={veiculo.nome} />
                                                    : <SilhuetaCarrinha />}
                                            </div>
                                            <div className="vehicle-corpo">
                                                <strong className="vehicle-nome">{veiculo.nome}</strong>
                                                <ChapaMatricula matricula={veiculo.matricula} />
                                            </div>
                                            <div className="vehicle-acoes">
                                                <button className="btn-icon-small delete" title="Eliminar"
                                                    onClick={e => { e.stopPropagation(); handleDelete(veiculo.id, veiculo.nome); }}><OrcamentoIcon name="trash" fallback="🗑️" /></button>
                                            </div>
                                        </div>
                                        {/* Fora do cartão: assim não precisa de travar o
                                            clique que abre a edição. */}
                                        <button type="button" className="btn-kms"
                                            onClick={() => abrirKms(veiculo)}>
                                            Registar kms
                                        </button>
                                        <button type="button" className="btn-kms"
                                            onClick={() => abrirEventos(veiculo)}>
                                            Registar Eventos
                                        </button>
                                        <button type="button" className="btn-kms"
                                            onClick={() => abrirConsulta(veiculo)}>
                                            Consulta kms e eventos
                                        </button>
                                        {/* Resumo por baixo dos botões: o que se quer saber
                                            de uma viatura sem ter de a abrir. */}
                                        <dl className="vehicle-resumo">
                                            {[
                                                ['seguro', 'Seguro:', veiculo.validade_seguro],
                                                ['inspecao', 'Inspeção:', veiculo.validade_inspecao],
                                            ].map(([campo, etiqueta, valor]) => {
                                                const arde = alertaPorTratar(veiculo, campo);
                                                return (
                                                    <div key={campo}>
                                                        <dt>{etiqueta}</dt>
                                                        <dd>
                                                            <span className={'veiculo-media-valor' + (arde ? ' prazo-alerta' : '')}
                                                                title={arde ? 'Falta um mês ou menos' : undefined}>
                                                                {formatDate(valor)}
                                                            </span>
                                                            {/* O botão só existe enquanto houver vermelho. */}
                                                            {arde && (
                                                                <button type="button" className="btn-tratado"
                                                                    title="Marcar como tratado"
                                                                    onClick={e => { e.stopPropagation(); marcarTratado(veiculo, campo); }}><LiquidText>✓</LiquidText></button>
                                                            )}
                                                        </dd>
                                                    </div>
                                                );
                                            })}
                                            {(() => {
                                                const rev = kmsParaRevisao(veiculo);
                                                const arde = alertaPorTratar(veiculo, 'revisao');
                                                const dataRev = veiculo.proxima_revisao_data;
                                                return (
                                                    <div>
                                                        <dt>Revisão em:</dt>
                                                        <dd>
                                                            <span className={arde ? 'prazo-alerta' : ''}
                                                                title={rev.ultrapassado ? 'A revisão já foi ultrapassada'
                                                                    : arde ? 'Faltam 2500 km ou menos' : undefined}>
                                                                <LiquidText>{rev.texto}</LiquidText>
                                                            </span>
                                                            {/* A data só aparece quando existe: é a
                                                                alternativa aos quilómetros, não um
                                                                segundo prazo a cumprir. */}
                                                            {/* As duas linhas existem sempre, mesmo sem data:
                                                                ficam invisíveis mas ocupam o espaço. É o que
                                                                permite ter as etiquetas alinhadas E os cartões
                                                                a fechar todos à mesma altura — sem isto, um
                                                                resumo mais alto desalinhava a coluna toda. */}
                                                            <span className="revisao-ou-cartao"
                                                                aria-hidden={!dataRev}
                                                                style={dataRev ? undefined : { visibility: 'hidden' }}>ou</span>
                                                            <span className={`revisao-data ${arde && dataRev && prazoEmAlerta(dataRev) ? 'prazo-alerta' : ''}`}
                                                                aria-hidden={!dataRev}
                                                                style={dataRev ? undefined : { visibility: 'hidden' }}
                                                                title={dataRev ? 'Data limite da revisão, se os quilómetros não forem atingidos antes' : undefined}>
                                                                {dataRev ? formatDate(dataRev) : '—'}
                                                            </span>
                                                            {arde && (
                                                                <button type="button" className="btn-tratado"
                                                                    title="Marcar como tratada"
                                                                    onClick={e => { e.stopPropagation(); marcarTratado(veiculo, 'revisao'); }}><LiquidText>✓</LiquidText></button>
                                                            )}
                                                        </dd>
                                                    </div>
                                                );
                                            })()}
                                            {/* Consumo médio, vindo dos abastecimentos lançados nas
                                                despesas de cartão. Enquanto não houver dois, diz o que
                                                falta em vez de mostrar um número que não existe. */}
                                            {(() => {
                                                const c = veiculo.consumo;
                                                const tem = c && c.consumo_medio != null;
                                                /* "L/100" e não "L/100 km": num cartão estreito o "km"
                                                   empurrava o número para outra linha, e ninguém lê
                                                   litros aos cem sem ser por quilómetro. O detalhe, que
                                                   tem espaço, continua a escrever a unidade por extenso. */
                                                const litros = (v) => `${v.toFixed(2).replace('.', ',')} L/100`;
                                                const semDados = (
                                                    <span style={{ color: '#94a3b8' }}>{c ? `${c.abastecimentos} abast.` : '—'}</span>
                                                );
                                                /* O último abastecimento ao lado da média é o que
                                                   denuncia o desvio: um consumo que salta acima da
                                                   média da viatura é sinal de avaria, de condução ou
                                                   de um abastecimento mal lançado. Só se pinta quando
                                                   o desvio passa de 15% — abaixo disso é a variação
                                                   normal entre percursos de cidade e de estrada. */
                                                const desvio = c?.desvio ?? null;
                                                const arde = alertaPorTratar(veiculo, 'consumo');
                                                const cor = desvio == null ? undefined
                                                    : desvio > 0.15 ? '#ff0000'
                                                        : desvio < -0.15 ? '#166534' : undefined;
                                                return (
                                                    <>
                                                        <div>
                                                            <dt>Média:</dt>
                                                            <dd>
                                                                <span className="veiculo-media-valor" title={tem
                                                                    ? `${c.abastecimentos} abastecimentos, ${c.intervalos} intervalo(s), ${fmtInteiro(c.km_medidos)} km medidos`
                                                                    + (c.custo_por_km ? ` · ${c.custo_por_km.toFixed(3).replace('.', ',')} €/km` : '')
                                                                    + (c.intervalos_ignorados ? ` · ${c.intervalos_ignorados} intervalo(s) de fora por salto grande de kms` : '')
                                                                    : (c?.porque_nao || 'Ainda não há abastecimentos lançados nesta viatura')}>
                                                                    {tem ? litros(c.consumo_medio) : semDados}
                                                                </span>
                                                            </dd>
                                                        </div>
                                                        <div>
                                                            <dt>Última média:</dt>
                                                            <dd>
                                                                <span className={arde ? 'prazo-alerta' : ''}
                                                                    style={cor ? { color: cor } : undefined}
                                                                    title={desvio == null ? 'Aparece a partir do terceiro abastecimento'
                                                                        : `${desvio > 0 ? '+' : ''}${(desvio * 100).toFixed(0)}% face ao consumo normal desta viatura`
                                                                        + (c.media_anterior ? ` (${litros(c.media_anterior)} até aqui)` : '')}>
                                                                    {c && c.ultimo_consumo != null ? litros(c.ultimo_consumo) : semDados}
                                                                </span>
                                                                {/* A bomba só aparece quando há desvio por ver; some
                                                                    ao dar por visto e volta no abastecimento seguinte
                                                                    se o desvio se mantiver. */}
                                                                {arde && (
                                                                    <>
                                                                        <span className="alerta-consumo" aria-hidden="true"
                                                                            title={c.alerta === 'grave' ? 'Desvio grande face ao normal' : 'Consumo fora do normal'}><LiquidText>⛽</LiquidText></span>
                                                                        <button type="button" className="btn-tratado"
                                                                            title="Dar o desvio por visto"
                                                                            onClick={e => { e.stopPropagation(); marcarTratado(veiculo, 'consumo'); }}><LiquidText>✓</LiquidText></button>
                                                                    </>
                                                                )}
                                                            </dd>
                                                        </div>
                                                    </>
                                                );
                                            })()}
                                        </dl>
                                    </div>
                                ))}
                                {/* Depois da frota da casa, um cartão só: o registo das
                                    alugadas. Uma alugada entra e sai em semanas e não tem
                                    seguro, inspeção nem revisão da empresa — dar-lhe um
                                    cartão como aos outros enchia a fila de viaturas que
                                    não são nossas e cujos prazos não nos dizem respeito.
                                    Ficam todas atrás deste, em preto para se distinguir
                                    das fotografias das outras. */}
                                <div className="vehicle-item">
                                    <div className="vehicle-card"
                                        role="button" tabIndex={0}
                                        title={alugadas.length === 0 ? 'Ver as viaturas alugadas — nenhuma de momento'
                                            : `Ver as viaturas alugadas — ${alugadas.length}`}
                                        onClick={() => setVerAlugadas(true)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                setVerAlugadas(true);
                                            }
                                        }}>
                                        <div className="vehicle-foto">
                                            <CarrinhaAlugada />
                                        </div>
                                        <div className="vehicle-corpo">
                                            <strong className="vehicle-nome">Viaturas Alugadas</strong>
                                            <ChapaMatricula matricula="ALUGUER" titulo="Viaturas alugadas" />
                                        </div>
                                    </div>
                                    {/* O mesmo retângulo dos outros cartões, mas com uma linha por
                                        alugada em vez dos prazos: seguro, inspeção e revisão são
                                        obrigações da empresa e uma alugada não as tem. De cada uma
                                        fica o que dela interessa — a chapa e o consumo a que anda.

                                        A chapa é um botão: leva direto aos abastecimentos daquela
                                        viatura. O cartão por cima abre a lista de todas, e desta
                                        forma salta-se o passo do meio quando já se sabe qual é. */}
                                    <div className="vehicle-resumo resumo-alugadas">
                                        {/* O ano à cabeça e os doze meses por baixo, cada um a
                                            ligar e a desligar por si — é o que permite escolher
                                            Janeiro e Março sem levar Fevereiro no meio. Com Shift
                                            apanha-se a faixa toda de uma vez.

                                            O span não é distração: a regra que desfaz o
                                            "display: contents" apanha os divs todos que sejam
                                            filhos diretos, e o filtro precisa de ficar em flex. */}
                                        <span className="alugadas-filtro">
                                            <select value={anoAlugadas} aria-label="Ano"
                                                onChange={e => setAnoAlugadas(Number(e.target.value))}>
                                                {anosAlugadas.map(a => <option key={a} value={a}>{a}</option>)}
                                            </select>
                                            <div className="alugadas-meses">
                                                {MESES.map((nome, i) => {
                                                    const m = i + 1;
                                                    const ligado = mesesAlugadas.includes(m);
                                                    return (
                                                        <button key={m} type="button"
                                                            className={`alugadas-mes${ligado ? ' on' : ''}`}
                                                            aria-pressed={ligado}
                                                            title={`${nome}${ligado ? ' — clica para tirar' : ''}. Com Shift apanha desde o último que tocaste.`}
                                                            onClick={e => alternarMesAlugadas(m, e.shiftKey)}>
                                                            {m}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </span>
                                        {/* A lista desliza dentro do retângulo. Com quatro ou cinco
                                            alugadas ao mesmo tempo ela crescia para baixo, e como as
                                            colunas da grelha esticam todas à altura da mais alta, era
                                            este cartão a mandar na altura da frota inteira. */}
                                        <div className="alugadas-lista">
                                        {mesesAlugadas.length === 0 ? (
                                            <p className="alugadas-vazio">Escolhe um mês</p>
                                        ) : alugadasDoPeriodo.length === 0 && (
                                            <p className="alugadas-vazio">
                                                {mesesAlugadas.length === 1 ? 'Nenhuma neste mês' : 'Nenhuma nestes meses'}
                                            </p>
                                        )}
                                        {alugadasDoPeriodo.map(({ v, km, litros, quantos, consumo, soltos }) => (
                                            <div key={v.id} className="alugada-linha">
                                                {/* Matrícula em texto e não na chapa desenhada: aqui ela
                                                    é o nome da linha, e a chapa — que faz sentido no
                                                    cartão de uma viatura — só rouba altura a um
                                                    retângulo onde cabem várias. Continua a ser botão:
                                                    leva direto aos abastecimentos daquela viatura. */}
                                                <button type="button" className="alugada-chapa"
                                                    title={`Ver os abastecimentos de ${v.matricula}`}
                                                    onClick={() => { setVerAlugadas(true); abrirAlugada(v); }}>
                                                    {v.matricula}
                                                </button>
                                                {/* Os quilómetros e o consumo com a mesma etiqueta dos
                                                    cartões da frota, para se lerem da mesma maneira. */}
                                                {quantos === 0 ? (
                                                    /* Ainda não há consumo para mostrar: um consumo precisa
                                                       de dois abastecimentos, que é onde há distância
                                                       medida. Dois traços e uma data por cima deles não
                                                       dizem isso — dizem só que falta alguma coisa. Uma
                                                       frase diz. */
                                                    <span className="alugada-media"
                                                        title={`${soltos.length} abastecimento(s) registado(s) no que está escolhido · ${soltos.reduce((s, a) => s + Number(a.litros || 0), 0).toFixed(2).replace('.', ',')} L. O consumo só se mede entre dois abastecimentos.`}>
                                                        <span className="alugada-parte alugada-solto">
                                                            {soltos.length === 1
                                                                ? 'Ainda só existe 1 registo'
                                                                : `Ainda só existem ${soltos.length} registos`}
                                                        </span>
                                                    </span>
                                                ) : (
                                                    /* Os quilómetros e o consumo com a mesma etiqueta dos
                                                       cartões da frota, para se lerem da mesma maneira. */
                                                    <span className="alugada-media"
                                                        title={`${quantos} abastecimento(s) no que está escolhido · ${fmtInteiro(km)} km · ${litros.toFixed(2).replace('.', ',')} L`}>
                                                        <span className="alugada-parte">
                                                            Kms percorridos:{' '}
                                                            {km > 0 ? fmtInteiro(km) : <span className="alugada-sem">—</span>}
                                                        </span>
                                                        <span className="alugada-parte">
                                                            Média:{' '}
                                                            {consumo != null ? fmtConsumo(consumo)
                                                                : <span className="alugada-sem">—</span>}
                                                        </span>
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                        </div>
                                    </div>
                                </div>
                            </div>}
                        </div>

                        {/* TRIPS SECTION (70%) */}
                        <div className="trips-section">
                            <div className="trips-header">
                                <h3>Registo de Viagens</h3>
                            </div>

                            {/* INLINE TRIP FORM */}
                            <div className="trip-form-card">
                                <form onSubmit={handleAddTrip} className="trip-form-inline">
                                    <div className="form-group-inline">
                                        <label>Data</label>
                                        <input
                                            type="date"
                                            value={newTrip.data_viagem}
                                            onChange={e => setNewTrip({ ...newTrip, data_viagem: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="form-group-inline">
                                        <label>Veículo</label>
                                        <select
                                            value={newTrip.veiculo_id}
                                            onChange={e => setNewTrip({ ...newTrip, veiculo_id: e.target.value })}
                                            required
                                        >
                                            <option value="">Selecione...</option>
                                            {veiculos.map(v => (
                                                <option key={v.id} value={v.id}>{v.nome} ({v.matricula})</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group-inline">
                                        <label>Empresa</label>
                                        <select
                                            value={newTrip.empresa_id}
                                            onChange={e => setNewTrip({ ...newTrip, empresa_id: e.target.value })}
                                            required
                                        >
                                            <option value="">Selecione...</option>
                                            {empresas.map(emp => (
                                                <option key={emp.id} value={emp.id}>{emp.nome}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group-inline small">
                                        <label>Kms</label>
                                        <input
                                            type="number"
                                            placeholder="0"
                                            value={newTrip.kms_percorridos}
                                            onChange={e => setNewTrip({ ...newTrip, kms_percorridos: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="form-group-inline small">
                                        <label>Portagens (€)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            placeholder="0.00"
                                            value={newTrip.valor_portagens}
                                            onChange={e => setNewTrip({ ...newTrip, valor_portagens: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group-inline">
                                        <label>Destino</label>
                                        <input
                                            type="text"
                                            placeholder="Ex: Lisboa"
                                            value={newTrip.destino}
                                            onChange={e => setNewTrip({ ...newTrip, destino: e.target.value })}
                                        />
                                    </div>
                                    <button type="submit" className="btn-add-trip">+ Adicionar</button>
                                </form>
                            </div>

                            {/* FILTERS BAR */}
                            <div className="filters-bar">
                                <div className="filter-group">
                                    <label>Veículo</label>
                                    <select value={filterVeiculo} onChange={e => setFilterVeiculo(e.target.value)}>
                                        <option value="all">Todos</option>
                                        {veiculos.map(v => (
                                            <option key={v.id} value={v.id}>{v.nome}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="filter-group">
                                    <label>Mês</label>
                                    <select value={filterMes} onChange={e => setFilterMes(e.target.value)}>
                                        {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                                            <option key={m} value={m}>
                                                {new Date(2000, m - 1).toLocaleString('pt-PT', { month: 'long' })}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="filter-group">
                                    <label>Ano</label>
                                    <select value={filterAno} onChange={e => setFilterAno(e.target.value)}>
                                        {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
                                            <option key={y} value={y}>{y}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="totals-display">
                                    <span><strong>Total Kms:</strong> {totalKms.toLocaleString()}</span>
                                    <span><strong>Total Portagens:</strong> {fmtMoney(totalPortagens)}</span>
                                </div>
                            </div>

                            {/* TRIPS TABLE */}
                            <div className="trips-table-container">
                                {loading ? (
                                    <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                                        Carregando registos...
                                    </div>
                                ) : registos.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                                        Sem registos para os filtros selecionados.
                                    </div>
                                ) : (
                                    <table className="trips-table standard-table">
                                        <thead>
                                            <tr>
                                                <th>Data</th>
                                                <th>Veículo</th>
                                                <th>Empresa</th>
                                                <th>Kms</th>
                                                <th>Portagens</th>
                                                <th>Destino</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {registos.map(reg => (
                                                <tr key={reg.id}>
                                                    <td>{formatDate(reg.data_viagem)}</td>
                                                    <td>
                                                        <strong>{reg.veiculo_nome}</strong>
                                                        <span className="matricula-tag">{reg.veiculo_matricula}</span>
                                                    </td>
                                                    <td><span className="empresa-tag">{reg.nome_empresa}</span></td>
                                                    <td>{parseInt(reg.kms_percorridos).toLocaleString()} km</td>
                                                    <td>{parseFloat(reg.valor_portagens) > 0 ? fmtMoney(reg.valor_portagens) : '-'}</td>
                                                    <td style={{ fontSize: '0.85rem', color: '#666' }}>{reg.destino || '-'}</td>
                                                    <td>
                                                        <button
                                                            className="btn-icon-delete"
                                                            onClick={() => handleDeleteTrip(reg.id)}
                                                            title="Apagar"
                                                        >
                                                            <OrcamentoIcon name="trash" fallback="🗑️" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* CONSULTA DE QUILÓMETROS POR PERÍODO */}
            {modalConsulta && (
                <LiquidDialog onClose={() => setModalConsulta(null)} className="modal-overlay modal-kms-overlay" onClick={() => setModalConsulta(null)}>
                    <div className="modal-content modal-kms" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Consulta kms e eventos {modalConsulta.nome}</h2>
                            <button className="close-btn" onClick={() => setModalConsulta(null)}>&times;</button>
                        </div>
                        <div className="kms-form consulta-form">
                            <div className="form-group">
                                <label>Consultar</label>
                                <select value={consulta.tipo}
                                    onChange={e => setConsulta({ tipo: e.target.value, ano: '', mes: '' })}>
                                    <option value="kms">Quilómetros</option>
                                    <option value="eventos">Eventos</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Ano</label>
                                <select value={consulta.ano}
                                    onChange={e => setConsulta({ ...consulta, ano: e.target.value, mes: '' })}>
                                    <option value="">TODOS</option>
                                    {anosDisponiveis.map(a => <option key={a} value={a}>{a}</option>)}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Mês</label>
                                {/* Sem ano escolhido, um mês sozinho misturava agosto de
                                    anos diferentes na mesma lista. */}
                                <select value={consulta.mes} disabled={!consulta.ano}
                                    onChange={e => setConsulta({ ...consulta, mes: e.target.value })}>
                                    <option value="">TODOS</option>
                                    {MESES.map((nome, i) => (
                                        <option key={nome} value={i + 1}>{nome}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        {resumoConsulta && (
                            <div className="kms-resumo">
                                <div>
                                    <span>N.º Leituras<br />registadas</span>
                                    <strong>{resumoConsulta.n}</strong>
                                </div>
                                <div>
                                    <span>Primeira<br />leitura kms</span>
                                    <strong>{formatDate(resumoConsulta.primeira.data)}</strong>
                                    <strong>{fmtInteiro(resumoConsulta.primeira.kms)} km</strong>
                                </div>
                                <div>
                                    <span>Última<br />leitura kms</span>
                                    <strong>{formatDate(resumoConsulta.ultima.data)}</strong>
                                    <strong>{fmtInteiro(resumoConsulta.ultima.kms)} km</strong>
                                </div>
                                <div>
                                    {/* Quebra explícita: "Percorridos" em cima e "no período"
                                        por baixo, para caber na mesma linha das outras. */}
                                    <span>Percorridos<br />no período</span>
                                    <strong>{fmtInteiro(resumoConsulta.percorridos)} km</strong>
                                </div>
                            </div>
                        )}
                        <div className="kms-historico">
                            {consultaCarregando ? (
                                <p className="kms-vazio">A carregar…</p>
                            ) : consultaLista.length === 0 ? (
                                <p className="kms-vazio">
                                    {consulta.tipo === 'kms'
                                        ? 'Não há leituras neste período.'
                                        : 'Não há eventos neste período.'}
                                </p>
                            ) : consulta.tipo === 'kms' ? (
                                <table className="kms-tabela">
                                    <thead>
                                        <tr><th style={{ width: '3.2rem' }}>Quem</th><th>Data</th><th>Quilómetros</th><th>Desde a anterior</th><th>Valor</th><th>Consumo</th><th style={{ width: '2.4rem' }}></th></tr>
                                    </thead>
                                    <tbody>
                                        {consultaLista.map((r, i) => {
                                            const anterior = consultaLista[i - 1];
                                            const delta = anterior ? r.kms - anterior.kms : null;
                                            /* O consumo só existe nas leituras que vieram de um
                                               abastecimento, e mede o intervalo desde o abastecimento
                                               ANTERIOR — que pode estar várias leituras atrás. As
                                               leituras avulsas do conta-quilómetros não têm litros,
                                               logo não têm consumo. */
                                            return (
                                                <tr key={r.id}>
                                                    <td title={r.quem || 'Não se sabe quem registou'}
                                                        style={{ color: '#64748b', whiteSpace: 'nowrap' }}>
                                                        {iniciais(r.quem)}
                                                    </td>
                                                    <td>{formatDate(r.data)}</td>
                                                    <td>{fmtInteiro(r.kms)} km</td>
                                                    <td>{delta == null ? '—' : `+${fmtInteiro(delta)} km`}</td>
                                                    {/* O que foi pago. Só existe nas leituras que vieram de
                                                        um abastecimento — as leituras avulsas do
                                                        conta-quilómetros não têm despesa por trás. */}
                                                    <td style={{ whiteSpace: 'nowrap' }}>
                                                        {r.valor != null ? fmtMoney(r.valor) : '—'}
                                                    </td>
                                                    <td title={r.consumo != null
                                                        ? `${r.litros} L em ${fmtInteiro(r.km_intervalo)} km desde o abastecimento anterior`
                                                        : r.litros != null
                                                            ? 'Primeiro abastecimento: não há intervalo anterior para medir'
                                                            : 'Leitura sem abastecimento'}>
                                                        {r.consumo != null
                                                            ? `${r.consumo.toFixed(2).replace('.', ',')} L/100 km`
                                                            : r.litros != null
                                                                ? <span style={{ color: '#94a3b8' }}><LiquidText>⛽ </LiquidText>{r.litros} L</span>
                                                                : '—'}
                                                    </td>
                                                    {/* Pedir confirmação a quem registou. Aparece só quando
                                                        há telemóvel na ficha — sem número não há a quem
                                                        perguntar, e um botão que não faz nada é pior do que
                                                        botão nenhum. Abre a conversa com a mensagem escrita;
                                                        o enviar continua a ser um clique de quem pergunta. */}
                                                    <td style={{ textAlign: 'center' }}>
                                                        {paraWhatsApp(r.quem_telefone) ? (
                                                            <a className="btn-icon-small"
                                                               target="_blank" rel="noopener"
                                                               title={`Pedir a ${r.quem} que confirme os quilómetros${r.litros != null ? ' e os litros' : ''} deste registo`}
                                                               href={`https://wa.me/${paraWhatsApp(r.quem_telefone)}?text=${encodeURIComponent(textoConfirmacao(modalConsulta?.nome || modalConsulta?.matricula || '', r))}`}>
                                                                <IconeMensagem />
                                                            </a>
                                                        ) : null}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            ) : (
                                <table className="kms-tabela">
                                    <thead>
                                        <tr><th>Data</th><th>Ocorrência</th><th className="col-valor">Valor</th></tr>
                                    </thead>
                                    <tbody>
                                        {consultaLista.map(r => (
                                            <tr key={r.id}>
                                                <td className="col-data">{formatDate(r.data)}</td>
                                                <td className="col-texto">{r.descricao}</td>
                                                <td className="col-valor">{r.valor != null ? fmtMoney(r.valor) : '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    {totalEventos != null && (
                                        <tfoot>
                                            <tr className="linha-total">
                                                {/* Célula da data vazia, para o texto cair
                                                    debaixo da coluna da ocorrência. */}
                                                <td className="col-data"></td>
                                                <td className="col-texto">TOTAL GASTO NO PERÍODO SELECIONADO</td>
                                                <td className="col-valor">{fmtMoney(totalEventos)}</td>
                                            </tr>
                                        </tfoot>
                                    )}
                                </table>
                            )}
                        </div>
                    </div>
                </LiquidDialog>
            )}

            {/* REGISTO DE EVENTOS */}
            {modalEventos && (
                <LiquidDialog onClose={() => setModalEventos(null)} className="modal-overlay modal-kms-overlay" onClick={() => setModalEventos(null)}>
                    <div className="modal-content modal-kms" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Eventos {modalEventos.nome}</h2>
                            <button className="close-btn" onClick={() => setModalEventos(null)}>&times;</button>
                        </div>
                        <form onSubmit={gravarEvento} className="evento-form">
                            <div className="evento-campos">
                                <div className="form-group">
                                    <label>Data</label>
                                    <input type="date" required value={eventoForm.data}
                                        onChange={e => setEventoForm({ ...eventoForm, data: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label>Ocorrência</label>
                                    <input type="text" required value={eventoForm.descricao}
                                        placeholder="Ex: revisão aos 150.000 km"
                                        onChange={e => setEventoForm({ ...eventoForm, descricao: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label>Valor</label>
                                    {/* Opcional: nem todo o acontecimento tem custo. */}
                                    <div className="campo-euro">
                                        {/* Campo de texto e não numérico: um input[type=number]
                                            não aceita o ponto dos milhares nem a vírgula
                                            decimal, que é como se escreve dinheiro em
                                            português. Enquanto se escreve fica como está;
                                            ao sair, formata-se. */}
                                        <input type="text" inputMode="decimal" value={eventoForm.valor}
                                            placeholder="0,00"
                                            onChange={e => setEventoForm({ ...eventoForm, valor: e.target.value })}
                                            onBlur={e => {
                                                const n = montanteParaNumero(e.target.value);
                                                setEventoForm(f => ({
                                                    ...f,
                                                    valor: n === null || !Number.isFinite(n)
                                                        ? e.target.value
                                                        : fmtValor(n),
                                                }));
                                            }} />
                                        <span>€</span>
                                    </div>
                                </div>
                            </div>
                            <button type="submit" className="btn-primary evento-registar">Registar</button>
                        </form>
                        {eventoErro && <div className="kms-erro">{eventoErro}</div>}
                        <div className="kms-historico">
                            {eventosLista.length === 0 ? (
                                <p className="kms-vazio">Ainda não há eventos registados.</p>
                            ) : (
                                <table className="kms-tabela">
                                    <thead>
                                        <tr><th>Data</th><th>Ocorrência</th><th className="col-valor">Valor</th><th></th></tr>
                                    </thead>
                                    <tbody>
                                        {eventosLista.map(r => (
                                            <tr key={r.id}>
                                                <td className="col-data">{formatDate(r.data)}</td>
                                                <td className="col-texto">
                                                    {eventoEmEdicao?.id === r.id ? (
                                                        /* Enter grava, Escape desiste — numa correção
                                                           de uma linha, ir buscar botões era mais
                                                           trabalho do que a própria correção. */
                                                        <input type="text" className="evento-edicao" autoFocus
                                                            value={eventoEmEdicao.texto}
                                                            onChange={e => setEventoEmEdicao({ ...eventoEmEdicao, texto: e.target.value })}
                                                            onBlur={guardarEdicao}
                                                            onKeyDown={e => {
                                                                if (e.key === 'Enter') { e.preventDefault(); guardarEdicao(); }
                                                                if (e.key === 'Escape') setEventoEmEdicao(null);
                                                            }} />
                                                    ) : r.descricao}
                                                </td>
                                                <td className="col-valor">{r.valor != null ? fmtMoney(r.valor) : '—'}</td>
                                                <td className="col-acoes">
                                                    <button type="button" className="btn-icon-small"
                                                        title="Editar ocorrência"
                                                        onClick={() => setEventoEmEdicao({ id: r.id, texto: r.descricao })}><OrcamentoIcon name="edit" fallback="✏️" /></button>
                                                    <button type="button" className="btn-icon-small delete"
                                                        title="Apagar evento"
                                                        onClick={() => apagarEvento(r.id)}><OrcamentoIcon name="trash" fallback="🗑️" /></button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </LiquidDialog>
            )}

            {/* LEITURAS DO CONTA-QUILÓMETROS */}
            {modalKms && (
                <LiquidDialog onClose={() => setModalKms(null)} className="modal-overlay modal-kms-overlay" onClick={() => setModalKms(null)}>
                    <div className="modal-content modal-kms" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Quilómetros — {modalKms.nome}</h2>
                            <button className="close-btn" onClick={() => setModalKms(null)}>&times;</button>
                        </div>
                        <form onSubmit={gravarKms} className="kms-form">
                            <div className="form-group">
                                <label>Data</label>
                                <input type="date" required value={kmsForm.data}
                                    onChange={e => setKmsForm({ ...kmsForm, data: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label>Quilómetros</label>
                                <input type="number" min="0" step="1" required value={kmsForm.kms}
                                    placeholder="Ex: 148230"
                                    onChange={e => setKmsForm({ ...kmsForm, kms: e.target.value })} />
                            </div>
                            <button type="submit" className="btn-primary kms-registar">Registar</button>
                        </form>
                        {kmsErro && <div className="kms-erro">{kmsErro}</div>}
                        <div className="kms-historico">
                            {kmsLista.length === 0 ? (
                                <p className="kms-vazio">Ainda não há leituras registadas.</p>
                            ) : (
                                <table className="kms-tabela">
                                    <thead>
                                        <tr><th style={{ width: '3.2rem' }}>Quem</th><th>Data</th><th>Quilómetros</th><th>Desde a anterior</th><th></th></tr>
                                    </thead>
                                    <tbody>
                                        {kmsLista.map((r, i) => {
                                            // A lista vem da mais antiga para a mais recente,
                                            // por isso a leitura anterior é a que vem antes.
                                            const anterior = kmsLista[i - 1];
                                            const delta = anterior ? r.kms - anterior.kms : null;
                                            return (
                                                <tr key={r.id}>
                                                    <td title={r.quem || 'Não se sabe quem registou'}
                                                        style={{ color: '#64748b', whiteSpace: 'nowrap' }}>
                                                        {iniciais(r.quem)}
                                                    </td>
                                                    <td>{formatDate(r.data)}</td>
                                                    <td>{fmtInteiro(r.kms)} km</td>
                                                    <td className={delta != null && delta < 0 ? 'kms-negativo' : ''}>
                                                        {delta == null ? '—'
                                                            : `${delta >= 0 ? '+' : ''}${fmtInteiro(delta)} km`}
                                                    </td>
                                                    <td>
                                                        <button type="button" className="btn-icon-small delete"
                                                            title="Apagar leitura"
                                                            onClick={() => apagarKms(r.id)}><OrcamentoIcon name="trash" fallback="🗑️" /></button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </LiquidDialog>
            )}

            {/* VEHICLE MODAL (Create/Edit) */}
            {
                showModal && (
                    <LiquidDialog onClose={() => setShowModal(false)} className="modal-overlay">
                        <div className="modal-content modal-content-viatura">
                            <div className={`modal-header${editingVeiculo ? ' modal-header-viatura' : ''}`}>
                                {/* A editar, o cabeçalho identifica a viatura em vez de
                                    repetir a palavra "Editar": a foto, a marca e modelo e
                                    a chapa dizem logo em qual se está a mexer. O nome e a
                                    matrícula vêm do formulário, portanto acompanham o que
                                    se está a escrever. */}
                                {editingVeiculo ? (
                                    <div className="modal-viatura">
                                        <div className="modal-viatura-foto">
                                            {editingVeiculo.foto_url
                                                ? <img src={`${uploadsBase}${editingVeiculo.foto_url}`} alt={formData.nome || editingVeiculo.nome} />
                                                : <SilhuetaCarrinha />}
                                        </div>
                                        <strong className="modal-viatura-nome">{formData.nome || editingVeiculo.nome}</strong>
                                        {(formData.matricula || editingVeiculo.matricula) && (
                                            <ChapaMatricula matricula={formData.matricula || editingVeiculo.matricula} />
                                        )}
                                    </div>
                                ) : <h2><OrcamentoIcon name="vehicle" fallback="🚗" /> Novo Veículo</h2>}
                            </div>
                            <form onSubmit={handleSubmit}>
                                {/* A editar, a marca, o modelo e a matrícula já estão no
                                    cabeçalho e não se repetem em campos. Ao criar continuam
                                    cá: sem eles não havia como dar nome à viatura, e o
                                    servidor recusa uma viatura sem nome. */}
                                {!editingVeiculo && (
                                    <>
                                        <div className="form-group">
                                            <label>Nome / Marca / Modelo</label>
                                            <input
                                                type="text"
                                                required
                                                value={formData.nome}
                                                onChange={e => setFormData({ ...formData, nome: e.target.value })}
                                                placeholder="Ex: Toyota Hilux"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Matrícula</label>
                                            <input
                                                type="text"
                                                required
                                                value={formData.matricula}
                                                onChange={e => setFormData({ ...formData, matricula: e.target.value })}
                                                placeholder="00-AA-00"
                                            />
                                        </div>
                                    </>
                                )}
                                {/* Companhia, apólice e validade do seguro na mesma linha:
                                    são a mesma informação e lêem-se de uma vez. A companhia
                                    leva mais largura porque o nome é o mais comprido dos três. */}
                                <div className="linha-seguro">
                                    <div className="form-group">
                                        <label>Seguradora</label>
                                        <input
                                            type="text"
                                            value={formData.seguro}
                                            onChange={e => setFormData({ ...formData, seguro: e.target.value })}
                                            placeholder="Ex: Fidelidade"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Apólice</label>
                                        <input
                                            type="text"
                                            value={formData.apolice}
                                            onChange={e => setFormData({ ...formData, apolice: e.target.value })}
                                            placeholder="Ex: 123456789"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Validade Seguro</label>
                                        <input
                                            type="date"
                                            value={formData.validade_seguro}
                                            onChange={e => setFormData({ ...formData, validade_seguro: e.target.value })}
                                        />
                                    </div>
                                </div>
                                {/* Mesma grelha da linha de cima, para as caixas caírem
                                    debaixo da Seguradora e da Apólice. A terceira coluna
                                    fica vazia de propósito. */}
                                <div className="linha-inspecao">
                                    <div className="form-group">
                                        <label>Próxima Inspeção</label>
                                        <input
                                            type="date"
                                            value={formData.validade_inspecao}
                                            onChange={e => setFormData({ ...formData, validade_inspecao: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Próxima Revisão</label>
                                        {/* Dois gatilhos, e vale o que vier primeiro: um carro
                                            pouco usado chega à data sem chegar aos quilómetros,
                                            um muito usado chega aos quilómetros muito antes. */}
                                        <input
                                            type="text"
                                            value={formData.proxima_revisao}
                                            onChange={e => setFormData({ ...formData, proxima_revisao: e.target.value })}
                                            placeholder="Ex: 150.000 km"
                                        />
                                        <div className="revisao-ou">ou</div>
                                        <input
                                            type="date"
                                            value={formData.proxima_revisao_data || ''}
                                            onChange={e => setFormData({ ...formData, proxima_revisao_data: e.target.value })}
                                            title="Data limite da revisão, se os quilómetros não forem atingidos antes"
                                        />
                                    </div>
                                    {editingVeiculo && (
                                        <div className="form-group">
                                            <label>Último registo kms</label>
                                            {/* Só de leitura: o valor vem das leituras do
                                                conta-quilómetros e altera-se por lá, não
                                                aqui. */}
                                            <input
                                                type="text"
                                                readOnly
                                                className="campo-so-leitura"
                                                value={editingVeiculo.ultimo_km != null
                                                    ? `${fmtInteiro(editingVeiculo.ultimo_km)} km`
                                                    : '—'}
                                                title={editingVeiculo.ultimo_km_data
                                                    ? `Leitura de ${formatDate(editingVeiculo.ultimo_km_data)}`
                                                    : 'Ainda não há leituras registadas'}
                                            />
                                        </div>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                                    <button type="submit" className="btn-primary">
                                        {editingVeiculo ? 'Guardar Alterações' : 'Adicionar Veículo'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </LiquidDialog>
                )
            }

            {/* REGISTO DAS VIATURAS ALUGADAS

                Duas vistas na mesma janela: a lista das matrículas com o consumo
                de cada uma e, ao carregar numa, os abastecimentos dela por mês. */}
            {verAlugadas && (
                <LiquidDialog onClose={() => { setVerAlugadas(false); setAlugadaAberta(null); }} className="modal-overlay"
                    onClick={() => { setVerAlugadas(false); setAlugadaAberta(null); }}>
                    <div className="modal-content" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            {/* Sem marca nem modelo, a matrícula é o nome da viatura. */}
                            <h2>{alugadaAberta ? alugadaAberta.matricula : 'Viaturas Alugadas'}</h2>
                            <button className="btn-close"
                                onClick={() => { setVerAlugadas(false); setAlugadaAberta(null); }}><LiquidText>✕</LiquidText></button>
                        </div>

                        <div style={{ padding: '4px 20px 20px' }}>
                            {!alugadaAberta && alugadas.length === 0 && (
                                <p style={{ color: '#64748b', fontSize: '0.88rem', margin: 0 }}>
                                    Ainda não há nenhuma. Acrescentam-se ao lançar o abastecimento
                                    no telemóvel, em Despesas → Registo Manual → Combustível.
                                </p>
                            )}

                            {/* A lista: matrícula, marca e modelo, e o consumo médio. */}
                            {!alugadaAberta && alugadas.map(v => {
                                const c = v.consumo;
                                return (
                                    <div key={v.id}
                                        role="button" tabIndex={0}
                                        title={`Ver os abastecimentos de ${v.matricula}`}
                                        onClick={() => abrirAlugada(v)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); abrirAlugada(v); }
                                        }}
                                        style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
                                            padding: '11px 4px', borderTop: '1px solid #f1f5f9' }}>
                                        {/* Matrícula em texto, e não na chapa desenhada: aqui ela é
                                            o nome da linha, não a fotografia de uma viatura. Por baixo
                                            vai o que interessa saber de uma alugada — quantos
                                            quilómetros mediu e a que consumo —, com a mesma etiqueta
                                            dos cartões da frota, para se ler da mesma maneira. */}
                                        <div style={{ flex: 1 }}>
                                            <strong style={{ fontSize: '0.95rem', color: '#1e293b',
                                                letterSpacing: '.02em' }}>{v.matricula}</strong>
                                            <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 2 }}>
                                                {c && c.km_medidos > 0 ? `${fmtInteiro(c.km_medidos)} km` : 'sem quilómetros medidos'}
                                                {' · '}
                                                Média:{' '}
                                                <strong style={{ color: c && c.consumo_medio != null ? '#1e293b' : '#94a3b8' }}>
                                                    {c && c.consumo_medio != null
                                                        ? `${c.consumo_medio.toFixed(2).replace('.', ',')} L/100 km`
                                                        : `${(c && c.abastecimentos) || 0} abast.`}
                                                </strong>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}

                            {/* O detalhe: os abastecimentos, agrupados por mês. */}
                            {alugadaAberta && (
                                <>
                                    <button className="btn-secondary"
                                        style={{ marginBottom: 12, padding: '5px 12px', fontSize: '0.8rem' }}
                                        onClick={() => { setAlugadaAberta(null); setDetalheAlugada(null); }}>
                                        ← Todas as alugadas
                                    </button>
                                    {erroAlugada && <p style={{ color: '#b91c1c', fontSize: '0.85rem' }}>{erroAlugada}</p>}
                                    {!detalheAlugada && !erroAlugada && (
                                        <p style={{ color: '#64748b', fontSize: '0.85rem' }}>A carregar…</p>
                                    )}
                                    {detalheAlugada && porMes.length === 0 && (
                                        <p style={{ color: '#64748b', fontSize: '0.85rem', margin: 0 }}>
                                            {detalheAlugada.abastecimentos.length < 2
                                                ? 'Só há um abastecimento. O consumo mede-se entre dois: falta o próximo.'
                                                : 'Os abastecimentos registados não têm quilómetros que permitam medir a distância.'}
                                        </p>
                                    )}
                                    {porMes.map(m => (
                                        <div key={m.chave} style={{ marginBottom: 14 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between',
                                                alignItems: 'baseline', borderBottom: '1px solid #e2e8f0', paddingBottom: 4 }}>
                                                <strong style={{ fontSize: '0.82rem', textTransform: 'uppercase',
                                                    letterSpacing: '.03em', color: '#475569' }}>
                                                    {MESES[Number(m.chave.slice(5, 7)) - 1]} {m.chave.slice(0, 4)}
                                                </strong>
                                                <span style={{ fontSize: '0.8rem', color: '#1e293b', fontWeight: 700 }}>
                                                    {fmtInteiro(m.km)} km · {m.litros.toFixed(2).replace('.', ',')} L
                                                    {m.consumo != null && ` · ${fmtConsumo(m.consumo)}`}
                                                </span>
                                            </div>
                                            {/* Data, quilómetros e litros daquele abastecimento, e à
                                                frente a média a que foram gastos. */}
                                            {m.linhas.map((i, k) => (
                                                <div key={k} style={{ display: 'flex', justifyContent: 'space-between',
                                                    fontSize: '0.82rem', padding: '5px 0', color: '#334155' }}>
                                                    <span>{formatDate(i.ate)}</span>
                                                    <span>{fmtInteiro(i.km)} km</span>
                                                    <span>{i.litros.toFixed(2).replace('.', ',')} L</span>
                                                    <span style={{ width: 100, textAlign: 'right' }}>
                                                        {fmtConsumo(i.consumo)}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                    {/* O primeiro abastecimento não fecha intervalo nenhum: só marca o
                                        ponto de partida. Dizê-lo evita a pergunta de porque é que há
                                        um abastecimento a mais do que linhas. */}
                                    {detalheAlugada && detalheAlugada.abastecimentos.length > 0 && (
                                        <p style={{ fontSize: '0.76rem', color: '#94a3b8', margin: 0 }}>
                                            {detalheAlugada.abastecimentos.length} abastecimento(s) registado(s).
                                            O primeiro serve de ponto de partida — a distância conta-se dele para a frente.
                                        </p>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </LiquidDialog>
            )}

            {/* EMPRESAS MODAL */}
            {
                showEmpresasManager && (
                    <EmpresasManagerModal
                        onClose={() => {
                            setShowEmpresasManager(false);
                            fetchEmpresas(); // Refresh companies list
                        }}
                    />
                )
            }
        </div >
    );
};

export default Veiculos;
